import { IRowNode, SelectionChangedEvent } from '@ag-grid-community/core'
import { AgGridReactProps } from '@ag-grid-community/react'
import { Button, DataTable, DataTablePagination } from 'components/common'
import { useNewSapBarcodeUnseenCount, usePageAccess, useQuery } from 'hooks'
import { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useLocation } from 'react-router-dom'
import {
  fetchNewSapBarcodes,
  markNewSapBarcodeSeen,
} from 'utils/api/newSapBarcodeApi'
import { filterEmptyValues } from 'utils/helpers'
import { handleError } from 'utils/helpers/handleError'
import { getColumns, FILTER_FIELD_NEW_SAP_BARCODE } from './utils'

const NewSapBarcodeTable = () => {
  const [response, setResponse] = useState<any>({})
  const [loading, setLoading] = useState<boolean>(false)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [submitting, setSubmitting] = useState<boolean>(false)

  const { isEditor } = usePageAccess()
  const { getAllQueries } = useQuery()
  const { search } = useLocation()
  const { refreshUnseenCount } = useNewSapBarcodeUnseenCount()

  const columns = useMemo(() => getColumns(isEditor), [isEditor])

  const getNewSapBarcodes = async () => {
    setLoading(true)
    // Reset selection whenever the listing reloads (pagination/filter change or
    // a post-action refetch) so it stays in sync with the freshly-mounted grid.
    setSelectedIds([])
    try {
      const queries = getAllQueries()
      const { limit, page } = queries
      // All filter controls persist under a single URL key; flatten them into
      // top-level query params (search, barcode, description, isSeen, from, to).
      const filters = filterEmptyValues(
        queries[FILTER_FIELD_NEW_SAP_BARCODE] || {},
      )

      const { data } = await fetchNewSapBarcodes(filters, {
        limit: limit || 10,
        page: page || 1,
      })

      setResponse(data)
    } catch (error) {
      setResponse({ docs: [] })
    } finally {
      setLoading(false)
    }

    // Keep the sidebar badge in step with the listing. Also covers the refetch
    // after "mark as read", where the count has just dropped.
    refreshUnseenCount()
  }

  useEffect(() => {
    getNewSapBarcodes()
  }, [search])

  // Only unseen rows can be selected — there is no "mark as unseen" endpoint.
  const isRowSelectable = useCallback(
    (node: IRowNode) => !node?.data?.isSeen,
    [],
  )

  const onSelectionChanged = useCallback((event: SelectionChangedEvent) => {
    setSelectedIds(event.api.getSelectedRows().map((row) => row._id))
  }, [])

  const onMarkAsRead = async () => {
    if (!selectedIds.length) return

    setSubmitting(true)
    const loadingToast = toast.loading('Loading...', { duration: Infinity })

    try {
      const { data } = await markNewSapBarcodeSeen(selectedIds)
      toast.success(data?.message || 'Marked as seen successfully', {
        id: loadingToast,
        duration: 3000,
      })
      // Refetch so rows reflect isSeen: true (and drop off when filtering by
      // isSeen=false). getNewSapBarcodes clears the selection.
      await getNewSapBarcodes()
    } catch (error) {
      handleError(error, loadingToast, {
        default: 'Failed to mark entries as seen.',
      })
    } finally {
      setSubmitting(false)
    }
  }

  // Editors get row-selection wired up; view-only users see the plain listing.
  const selectionProps: Partial<AgGridReactProps> = isEditor
    ? {
        rowSelection: 'multiple',
        suppressRowClickSelection: true,
        rowMultiSelectWithClick: false,
        isRowSelectable,
        onSelectionChanged,
      }
    : {}

  return (
    <>
      {isEditor && (
        <div className="mb-3 flex justify-end">
          <Button
            type="button"
            theme="success"
            buttonText={`Mark as read${
              selectedIds.length ? ` (${selectedIds.length})` : ''
            }`}
            disabled={!selectedIds.length}
            loading={submitting}
            onClick={onMarkAsRead}
          />
        </div>
      )}

      <DataTable
        columns={loading ? [] : columns}
        rows={loading ? null : response?.docs}
        identifier="_id"
        {...selectionProps}
      />

      <DataTablePagination
        currentPage={response?.page}
        totalPages={response?.totalPages}
        hasNextPage={response?.hasNextPage}
        hasPrevPage={response?.hasPrevPage}
        limit={response?.limit}
        nextPage={response?.nextPage}
        prevPage={response?.prevPage}
        disabled={loading}
      />
    </>
  )
}

export default NewSapBarcodeTable
